import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import styled from "styled-components";
import useSkinPath from "src/hooks/useSkinPath";
import {
  GRIFFLE_COLORS,
  GRIFFLE_FONT,
  GRIFFLE_FONT_BODY,
  GRIFFLE_TYPE,
  GRIFFLE_RADIUS,
  GRIFFLE_SHADOW,
  GRIFFLE_STAT_COLORS
} from "src/modules/griffle/griffleTheme";

export interface UpcomingAssignment {
  id: number;
  name: string;
  dueDate: string | Date;
  groups: string;
  usersCompleted: number;
  usersAssigned: number;
}

interface Props {
  assignments: UpcomingAssignment[];
  maxItems?: number;
}

const Wrapper = styled.div`
  background: ${GRIFFLE_COLORS.surface};
  border: 1px solid ${GRIFFLE_COLORS.border};
  border-radius: ${GRIFFLE_RADIUS.card};
  box-shadow: ${GRIFFLE_SHADOW.card};
  padding: 20px 24px;
  font-family: ${GRIFFLE_FONT_BODY};

  .ua-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .ua-title {
    font-family: ${GRIFFLE_FONT};
    font-weight: ${GRIFFLE_TYPE.weightSemi};
    font-size: ${GRIFFLE_TYPE.headingSm};
    color: ${GRIFFLE_COLORS.ink};
    margin: 0;
  }

  .ua-view-all {
    font-size: ${GRIFFLE_TYPE.small};
    font-weight: ${GRIFFLE_TYPE.weightMd};
    color: ${GRIFFLE_COLORS.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .ua-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ua-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    background: ${GRIFFLE_COLORS.pageBg};
    border-radius: ${GRIFFLE_RADIUS.button};
    text-decoration: none;
    color: inherit;
    transition: background 0.15s;

    &:hover {
      background: ${GRIFFLE_COLORS.border};
      text-decoration: none;
      color: inherit;
    }
  }

  .ua-due-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 46px;
    padding: 4px 8px;
    border-radius: ${GRIFFLE_RADIUS.badge};
    font-size: ${GRIFFLE_TYPE.xsmall};
    font-weight: ${GRIFFLE_TYPE.weightSemi};
    text-transform: uppercase;
    line-height: 1.2;

    .ua-due-day {
      font-size: 16px;
      font-weight: ${GRIFFLE_TYPE.weightBold};
    }
  }

  .ua-due-badge--soon {
    background: ${GRIFFLE_STAT_COLORS.books.bg};
    color: ${GRIFFLE_STAT_COLORS.books.text};
  }

  .ua-due-badge--normal {
    background: ${GRIFFLE_STAT_COLORS.students.bg};
    color: ${GRIFFLE_STAT_COLORS.students.text};
  }

  .ua-info {
    flex: 1;
    min-width: 0;
  }

  .ua-name {
    font-size: ${GRIFFLE_TYPE.body};
    font-weight: ${GRIFFLE_TYPE.weightMd};
    color: ${GRIFFLE_COLORS.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ua-meta {
    font-size: ${GRIFFLE_TYPE.small};
    color: ${GRIFFLE_COLORS.inkMuted};
    margin-top: 2px;
  }

  .ua-progress {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  .ua-progress-bar {
    width: 48px;
    height: 5px;
    background: ${GRIFFLE_COLORS.border};
    border-radius: 3px;
    overflow: hidden;
    position: relative;

    &::after {
      content: "";
      display: block;
      height: 100%;
      border-radius: 3px;
      transition: width 0.3s;
    }
  }

  .ua-progress-label {
    font-size: ${GRIFFLE_TYPE.xsmall};
    color: ${GRIFFLE_COLORS.inkMuted};
    white-space: nowrap;
  }

  .ua-empty {
    text-align: center;
    padding: 20px;
    color: ${GRIFFLE_COLORS.inkMuted};
    font-size: ${GRIFFLE_TYPE.body};
  }
`;

const UpcomingAssignments: React.FC<Props> = ({ assignments, maxItems = 5 }) => {
  const [t] = useTranslation();
  const { path } = useSkinPath();

  const now = moment();
  const upcoming = assignments
    .filter((a) => moment(a.dueDate).isAfter(now))
    .sort((a, b) => moment(a.dueDate).diff(moment(b.dueDate)))
    .slice(0, maxItems);

  const getDueBadgeClass = (dueDate: string | Date) => {
    const daysLeft = moment(dueDate).diff(now, "days");
    return daysLeft <= 3 ? "ua-due-badge--soon" : "ua-due-badge--normal";
  };

  return (
    <Wrapper>
      <div className="ua-header">
        <h3 className="ua-title">{t("dashboard_upcoming_assignments", "Upcoming Assignments")}</h3>
        <Link className="ua-view-all" to={path("/assignments")}>
          {t("dashboard_view_all_assignments", "View all")}
        </Link>
      </div>

      {upcoming.length === 0 ? (
        <div className="ua-empty">{t("dashboard_no_upcoming_assignments", "No upcoming assignments")}</div>
      ) : (
        <ul className="ua-list">
          {upcoming.map((a) => {
            const due = moment(a.dueDate);
            const percent = a.usersAssigned > 0 ? Math.round((a.usersCompleted / a.usersAssigned) * 100) : 0;
            const barColor = percent >= 75 ? "#28a745" : percent >= 40 ? "#ffc107" : "#dc3545";

            return (
              <li key={a.id}>
                <Link className="ua-item" to={path(`/assignments/${a.id}`)}>
                  <div className={`ua-due-badge ${getDueBadgeClass(a.dueDate)}`}>
                    <span className="ua-due-day">{due.format("D")}</span>
                    <span>{due.format("MMM")}</span>
                  </div>
                  <div className="ua-info">
                    <div className="ua-name">{a.name}</div>
                    <div className="ua-meta">
                      {(typeof a.groups === "string" ? a.groups : "") ||
                        t("dashboard_upcoming_no_group", "No group")}
                      {" · "}
                      {t("dashboard_upcoming_due_in", "Due in {{days}} days", {
                        days: due.diff(now, "days")
                      })}
                    </div>
                  </div>
                  <div className="ua-progress">
                    <div className="ua-progress-bar" style={{ position: "relative" }}>
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          height: "100%",
                          width: `${percent}%`,
                          background: barColor,
                          borderRadius: 3
                        }}
                      />
                    </div>
                    <span className="ua-progress-label">
                      {a.usersCompleted}/{a.usersAssigned}
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Wrapper>
  );
};

export default UpcomingAssignments;
